/**
 * API Key Modal Component
 * Prompts user to enter their Anthropic API key when none is configured
 */

import { t } from '../core/i18n';

export interface ApiKeyModalCallbacks {
  onSubmit: (apiKey: string) => void;
  onCancel?: () => void;
}

/**
 * Create the API key modal element
 */
export function createApiKeyModal(callbacks: ApiKeyModalCallbacks): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'pms-api-key-overlay';

  const modal = document.createElement('div');
  modal.className = 'pms-api-key-modal';

  modal.innerHTML = `
    <div class="pms-api-key-header">
      <div class="pms-api-key-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
        </svg>
      </div>
      <h2 class="pms-api-key-title">${t('apiKeyModal.title')}</h2>
    </div>
    <p class="pms-api-key-description">
      ${t('apiKeyModal.description')}
    </p>
    <div class="pms-api-key-input-wrapper">
      <input
        type="password"
        class="pms-api-key-input"
        placeholder="${t('apiKeyModal.placeholder')}"
        autocomplete="off"
        spellcheck="false"
      />
      <button type="button" class="pms-api-key-toggle" aria-label="${t('apiKeyModal.toggleVisibility')}">
        <svg class="pms-eye-open" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        <svg class="pms-eye-closed" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display:none">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
      </button>
    </div>
    <div class="pms-api-key-help">
      <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener">
        ${t('apiKeyModal.getApiKey')}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
      </a>
    </div>
    <div class="pms-api-key-error" style="display: none;"></div>
    <div class="pms-api-key-actions">
      <button type="button" class="pms-api-key-cancel">${t('apiKeyModal.cancel')}</button>
      <button type="button" class="pms-api-key-submit">${t('apiKeyModal.connect')}</button>
    </div>
    <div class="pms-api-key-footer">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
      ${t('apiKeyModal.footer')}
    </div>
  `;

  // Get references
  const input = modal.querySelector('.pms-api-key-input') as HTMLInputElement;
  const toggleBtn = modal.querySelector('.pms-api-key-toggle') as HTMLButtonElement;
  const submitBtn = modal.querySelector('.pms-api-key-submit') as HTMLButtonElement;
  const cancelBtn = modal.querySelector('.pms-api-key-cancel') as HTMLButtonElement;
  const errorDiv = modal.querySelector('.pms-api-key-error') as HTMLElement;
  const eyeOpen = modal.querySelector('.pms-eye-open') as SVGElement;
  const eyeClosed = modal.querySelector('.pms-eye-closed') as SVGElement;

  // Toggle password visibility
  toggleBtn.addEventListener('click', () => {
    if (input.type === 'password') {
      input.type = 'text';
      eyeOpen.style.display = 'none';
      eyeClosed.style.display = 'block';
    } else {
      input.type = 'password';
      eyeOpen.style.display = 'block';
      eyeClosed.style.display = 'none';
    }
  });

  // Validate and submit
  function validateAndSubmit() {
    const key = input.value.trim();

    // Basic validation
    if (!key) {
      showError(t('apiKeyModal.errorEmpty'));
      return;
    }

    if (!key.startsWith('sk-ant-')) {
      showError(t('apiKeyModal.errorFormat'));
      return;
    }

    if (key.length < 50) {
      showError(t('apiKeyModal.errorIncomplete'));
      return;
    }

    // Clear error and submit
    hideError();
    callbacks.onSubmit(key);
  }

  function showError(message: string) {
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
    input.classList.add('pms-api-key-input-error');
  }

  function hideError() {
    errorDiv.style.display = 'none';
    input.classList.remove('pms-api-key-input-error');
  }

  // Event listeners
  submitBtn.addEventListener('click', validateAndSubmit);

  input.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAndSubmit();
    }
  });

  input.addEventListener('input', () => {
    hideError();
  });

  cancelBtn.addEventListener('click', () => {
    callbacks.onCancel?.();
    overlay.remove();
  });

  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      callbacks.onCancel?.();
      overlay.remove();
    }
  });

  overlay.appendChild(modal);

  // Focus input when shown
  setTimeout(() => input.focus(), 100);

  return overlay;
}

/**
 * Show the API key modal
 */
export function showApiKeyModal(
  container: HTMLElement | ShadowRoot,
  callbacks: ApiKeyModalCallbacks
): HTMLElement {
  const modal = createApiKeyModal(callbacks);
  container.appendChild(modal);
  return modal;
}

/**
 * Hide/remove the API key modal
 */
export function hideApiKeyModal(modal: HTMLElement): void {
  modal.classList.add('pms-api-key-closing');
  setTimeout(() => modal.remove(), 200);
}
